import { allows, type ResolvedEntitlements } from "@giftforward/entitlements";
import { buildCsv, downloadCsv } from "@/lib/csv";

/**
 * The recipient-upload template, served client-side for Step 2's "Send on different dates" download
 * and mirroring the OneSchema templates the bulk-upload modal (BulkUploadModal) uses.
 *
 * Why generated here and not fetched from OneSchema: the "Download Excel template" button lives
 * INSIDE the OneSchema importer iframe, the SDK exposes no way to trigger it from outside, and we
 * hold only the embed JWT credentials — not a OneSchema REST API key — so there is no way to pull
 * the real file. The columns below reproduce it from the same fields instead. Headers are the
 * human-readable labels (not the underlying keys) so a filled-in file maps cleanly on re-upload:
 * OneSchema matches uploaded columns to template columns by header text.
 */

/** One template column; `proOnly` columns exist only in the Pro/Enterprise template, and `sendDate`
 *  columns are the per-recipient send schedule — dropped for a campaign that never sends. */
interface TemplateColumn {
  label: string;
  proOnly?: boolean;
  sendDate?: boolean;
}

// Column set and order. The three `proOnly` columns are exactly what the Pro/Enterprise template
// adds over Starter — recipient country and the per-recipient send date/time — which is the same
// split BulkUploadModal keys its template choice on. Keep this in step with the OneSchema templates
// if their columns change; there is no API to derive them from.
const TEMPLATE_COLUMNS: TemplateColumn[] = [
  { label: "Recipient First Name" },
  { label: "Recipient Last Name" },
  { label: "Recipient Email" },
  { label: "Recipient Company" },
  { label: "Recipient Country", proOnly: true },
  { label: "Sender First Name" },
  { label: "Sender Last Name" },
  { label: "Sender Email" },
  { label: "Sender Signature" },
  { label: "Sender Company" },
  { label: "Send On Date", proOnly: true, sendDate: true },
  { label: "Send at Time", proOnly: true, sendDate: true },
];

/**
 * True when the caller's plan gets the Pro/Enterprise template. The single source of truth for the
 * tier split, shared with BulkUploadModal so this download and the importer there never diverge: the
 * rich template is the one carrying the country + per-recipient send-date columns, so it follows the
 * entitlements those columns depend on rather than a plan name.
 */
export function recipientTemplateIsPro(ent: ResolvedEntitlements | undefined): boolean {
  return (
    !!ent &&
    allows(ent, "campaign_send", "scheduling", "per_recipient") &&
    allows(ent, "campaign_send", "regions", "international")
  );
}

/** The tier-appropriate template as a header-only CSV (no data rows — it's a blank template).
 *  `withSendDate=false` drops the per-recipient send schedule columns, for a manual "link to a list"
 *  campaign where GfG sends nothing. */
function buildRecipientTemplateCsv(isPro: boolean, withSendDate: boolean): string {
  const columns = TEMPLATE_COLUMNS.filter(
    (c) => (isPro || !c.proOnly) && (withSendDate || !c.sendDate),
  ).map((c) => ({
    label: c.label,
    value: () => "",
  }));
  return buildCsv(columns, []);
}

/**
 * Download the recipient-upload template matching the caller's tier. `withSendDate` defaults true (the
 * gfg "Send on different dates" download wants the schedule columns); pass false for a manual "link to
 * a list" campaign, which never sends per-recipient and so has no use for them.
 */
export function downloadRecipientTemplate(
  ent: ResolvedEntitlements | undefined,
  opts?: { withSendDate?: boolean },
): void {
  const isPro = recipientTemplateIsPro(ent);
  const withSendDate = opts?.withSendDate ?? true;
  const filename = isPro
    ? "gift-recipient-template-pro.csv"
    : "gift-recipient-template-starter.csv";
  downloadCsv(filename, buildRecipientTemplateCsv(isPro, withSendDate));
}
